import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { getBackofficeUserByEmail, BackofficeUser } from '@/mocks/backoffice/users';
import { getEmpresaIdsByUserId, getEmpresasByUserId } from '@/mocks/backoffice/empresaUsuarios';
import type { EmpresaUsuario } from '@/mocks/backoffice/empresaUsuarios';

const BO_AUTH_KEY = 'backoffice_auth';

export type BackofficeRole = 'admin' | 'suporte' | 'financeiro' | 'operador';

export interface BackofficeSession {
  isAuthenticated: boolean;
  userId?: string;
  nome?: string;
  email?: string;
  role?: BackofficeRole;
  avatar?: string;
  loginAt?: string;
  empresasPermitidas?: string[];
}

interface BackofficeAuthContextType {
  session: BackofficeSession;
  isAuthenticated: boolean;
  role: BackofficeRole | null;
  login: (email: string, senha: string) => { success: boolean; error?: string };
  logout: () => void;
  canAccess: (allowedRoles: BackofficeRole[]) => boolean;
  empresasDoUsuario: EmpresaUsuario[];
}

const BackofficeAuthContext = createContext<BackofficeAuthContextType | undefined>(undefined);

function getInitialSession(): BackofficeSession {
  try {
    const stored = localStorage.getItem(BO_AUTH_KEY);
    if (stored) {
      return JSON.parse(stored) as BackofficeSession;
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false };
}

export function BackofficeAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<BackofficeSession>(getInitialSession);
  const [empresasDoUsuario, setEmpresasDoUsuario] = useState<EmpresaUsuario[]>(() => {
    const stored = getInitialSession();
    if (stored.isAuthenticated && stored.userId) {
      return getEmpresasByUserId(stored.userId);
    }
    return [];
  });

  const login = useCallback((email: string, senha: string): { success: boolean; error?: string } => {
    const user = getBackofficeUserByEmail(email);
    if (!user) {
      return { success: false, error: 'Email não encontrado' };
    }
    if (!user.ativo) {
      return { success: false, error: 'Usuário inativo' };
    }
    if (senha !== '123456') {
      return { success: false, error: 'Senha incorreta' };
    }

    // Carregar empresas que este usuário pode acessar
    const empresasIds = getEmpresaIdsByUserId(user.id);
    const empresas = getEmpresasByUserId(user.id);

    const newSession: BackofficeSession = {
      isAuthenticated: true,
      userId: user.id,
      nome: user.nome,
      email: user.email,
      role: user.role,
      avatar: user.avatar,
      loginAt: new Date().toISOString(),
      empresasPermitidas: empresasIds,
    };
    localStorage.setItem(BO_AUTH_KEY, JSON.stringify(newSession));
    setSession(newSession);
    setEmpresasDoUsuario(empresas);
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(BO_AUTH_KEY);
    setSession({ isAuthenticated: false });
    setEmpresasDoUsuario([]);
  }, []);

  const canAccess = useCallback((allowedRoles: BackofficeRole[]): boolean => {
    if (!session.isAuthenticated || !session.role) return false;
    if (session.role === 'admin') return true;
    return allowedRoles.includes(session.role);
  }, [session]);

  return (
    <BackofficeAuthContext.Provider value={{
      session,
      isAuthenticated: session.isAuthenticated === true,
      role: session.role || null,
      login,
      logout,
      canAccess,
      empresasDoUsuario,
    }}>
      {children}
    </BackofficeAuthContext.Provider>
  );
}

export function useBackofficeAuth(): BackofficeAuthContextType {
  const context = useContext(BackofficeAuthContext);
  if (context === undefined) {
    throw new Error('useBackofficeAuth deve ser usado dentro de um BackofficeAuthProvider');
  }
  return context;
}

export function getBackofficeSession(): BackofficeSession {
  try {
    const stored = localStorage.getItem(BO_AUTH_KEY);
    if (stored) {
      return JSON.parse(stored) as BackofficeSession;
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false };
}